import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/sections/SectionHeading";
import { TESTIMONIALS } from "@/data/content";
import { Quote, ChevronLeft, ChevronRight, Gift, Coins } from "lucide-react";

const GiftCertificate = () => (
  <div className="relative w-full max-w-sm">
    <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-tr from-gold/25 to-gold-rose/20 blur-2xl" />
    <div className="relative overflow-hidden rounded-3xl border border-gold/40 bg-gradient-to-br from-charcoal-deep to-charcoal p-7 text-ivory shadow-2xl">
      <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gold/20 blur-2xl" />
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
          <Gift className="h-4 w-4" /> Gold Gift
        </span>
        <Coins className="h-6 w-6 text-gold" />
      </div>
      <p className="mt-8 text-xs uppercase tracking-[0.18em] text-ivory/50">Gifted to</p>
      <p className="font-serif text-2xl">Aai & Baba</p>
      <div className="mt-6 flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-ivory/50">Value</p>
          <p className="font-serif text-4xl text-gold-gradient">₹25,000</p>
          <p className="text-xs text-ivory/50">3.85g · 999.9 fine</p>
        </div>
        <p className="font-serif text-lg italic text-ivory/70">Shreedhan</p>
      </div>
      <div className="mt-6 border-t border-white/10 pt-4 text-[11px] text-ivory/50">
        "For a golden year ahead. — With love, Priya" · redeemable anytime
      </div>
    </div>
  </div>
);

export const Testimonials = () => {
  const [i, setI] = useState(0);
  const next = () => setI((p) => (p + 1) % TESTIMONIALS.length);
  const prev = () => setI((p) => (p - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const t = TESTIMONIALS[i];

  return (
    <section id="testimonials" data-testid="testimonials" className="relative bg-ivory-light py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-16 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <SectionHeading
              overline="Loved across borders"
              title="Golden stories from real savers"
              accentWord="Golden stories"
            />

            <div className="mt-10 min-h-[220px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.5 }}
                  data-testid={`testimonial-${i}`}
                >
                  <Quote className="h-10 w-10 text-gold/40" />
                  <p className="mt-4 font-serif text-2xl font-light leading-relaxed text-charcoal sm:text-3xl">
                    "{t.quote}"
                  </p>
                  <div className="mt-6">
                    <p className="font-semibold text-charcoal">{t.name}</p>
                    <p className="text-sm text-charcoal-soft">{t.role}</p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-8 flex items-center gap-3">
              <button
                data-testid="testimonial-prev"
                onClick={prev}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-200/60 bg-white/60 text-charcoal transition-colors hover:border-gold hover:text-gold"
                aria-label="Previous"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                data-testid="testimonial-next"
                onClick={next}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-200/60 bg-white/60 text-charcoal transition-colors hover:border-gold hover:text-gold"
                aria-label="Next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="ml-3 flex gap-1.5">
                {TESTIMONIALS.map((_, idx) => (
                  <span
                    key={idx}
                    className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-gold" : "w-1.5 bg-charcoal/20"}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <Reveal delay={0.1} className="flex justify-center">
            <GiftCertificate />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
